import { useCallback, useEffect, useState } from 'react';
import { getLiveCatalog, type XtreamCredentials } from '../api/xtreamClient';
import type { XtreamCategory, XtreamLiveStream, XtreamSeries, XtreamVodStream } from '../types/xtream';

export interface XtreamCatalog {
  liveCategories: XtreamCategory[];
  liveStreams: XtreamLiveStream[];
  vodStreams: XtreamVodStream[];
  seriesList: XtreamSeries[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useXtreamCatalog(creds: XtreamCredentials | null): XtreamCatalog {
  const [liveCategories, setLiveCategories] = useState<XtreamCategory[]>([]);
  const [liveStreams, setLiveStreams] = useState<XtreamLiveStream[]>([]);
  const [vodStreams, setVodStreams] = useState<XtreamVodStream[]>([]);
  const [seriesList, setSeriesList] = useState<XtreamSeries[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!creds) {
      setLiveCategories([]);
      setLiveStreams([]);
      setVodStreams([]);
      setSeriesList([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const catalog = await getLiveCatalog(creds);
        if (cancelled) return;
        setLiveCategories(catalog.categories);
        setLiveStreams(catalog.streams);
        setVodStreams([]);
        setSeriesList([]);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Nie udało się załadować listy kanałów');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [creds, tick]);

  return {
    liveCategories,
    liveStreams,
    vodStreams,
    seriesList,
    loading,
    error,
    reload,
  };
}
